import React, { useEffect, useRef, useState } from "react";

type DeferredSectionProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  rootMargin?: string;
  forceRender?: boolean;
};

const DeferredSection: React.FC<DeferredSectionProps> = ({
  children,
  rootMargin = "140% 0px",
  forceRender = false,
  style,
  ...props
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (forceRender) {
      setShouldRender(true);
    }
  }, [forceRender]);

  useEffect(() => {
    if (shouldRender) return;
    if (!sectionRef.current) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldRender(true);
        observer.disconnect();
      },
      { root: null, rootMargin, threshold: 0.01 },
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [rootMargin, shouldRender]);

  return (
    <div ref={sectionRef} style={style} {...props}>
      {shouldRender || forceRender ? children : null}
    </div>
  );
};

export default DeferredSection;
